import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import StudentService from '../../services/student.service';

const StudentForm = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  useEffect(() => {
    if (isEdit) {
      fetchStudent();
    }
  }, [id, isEdit]);

  const fetchStudent = async () => {
    setLoading(true);
    try {
      const response = await StudentService.get(id);
      const student = response.data?.data;
      setFormData({
        name: student.name || '',
        code: student.code || '',
      });
    } catch (error) {
      console.error('Error loading student:', error);
      showSnackbar(t('students.errorLoadingStudent'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) {
      showSnackbar(t('common.snackbar.pleaseFillAllRequiredFields'), 'error');
      return;
    }

    try {
      setLoading(true);
      if (isEdit) {
        await StudentService.update(id, formData);
        showSnackbar(t('students.studentUpdatedSuccess'));
      } else {
        await StudentService.create(formData);
        showSnackbar(t('students.studentCreatedSuccess'));
      }
      navigate('/students');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          (error.response?.data?.errors ? 
                            Object.values(error.response.data.errors).flat().join(', ') : 
                            `Error ${isEdit ? 'updating' : 'creating'} student`);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate('/students')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">
              {isEdit ? t('students.editStudent') : t('students.newStudent')}
            </Typography>
          </Box>
        }
        action={
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {isEdit ? t('students.updateStudent') : t('students.saveStudent')}
          </Button>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('students.studentName')}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('students.studentCode')}
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              required
              helperText={t('students.studentCodeHelper')}
            />
          </Grid>
        </Grid>
      </CardContent>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default StudentForm;
